const OPEN_CLASS = 'tutorial-hint--is-open';

/**
 * Decides which interaction model a device gets. A hovering pointer opens the panel on
 * enter; a touch device has to tap the term instead. The two models are mutually exclusive
 * because touch browsers emit mouseenter on tap as well.
 *
 * @returns {boolean} true when the primary pointer can hover
 */
export function supportsHover() {
    return typeof window.matchMedia === 'function' ? window.matchMedia('(hover: hover)').matches : true;
}

/**
 * Keeps the panel inside its column: the panel is centred on the term, which pushes it out
 * of the viewport whenever the term sits near an edge. The offset is applied as a custom
 * property so the stylesheet keeps ownership of the actual positioning.
 *
 * @param {HTMLElement} hint the wrapper carrying data-tutorial-hint
 * @param {HTMLElement} panel the element holding the screenshot
 * @param {number} margin minimum distance to the viewport edge
 * @returns {number} the horizontal correction in pixels
 */
export function resolvePanelShift(hint, panel, margin = 16) {
    const viewportWidth = document.documentElement.clientWidth;
    const hintBox = hint.getBoundingClientRect();
    const panelWidth = panel.offsetWidth;

    const centre = hintBox.left + hintBox.width / 2;
    const left = centre - panelWidth / 2;
    const right = centre + panelWidth / 2;

    if (left < margin) {
        return margin - left;
    }
    if (right > viewportWidth - margin) {
        return viewportWidth - margin - right;
    }

    return 0;
}

/**
 * Puts the panel above the term, unless the space there is already taken - by the sticky
 * page header, or by the top of a scrolled container such as an opened accordion.
 *
 * @param {HTMLElement} hint the wrapper carrying data-tutorial-hint
 * @param {HTMLElement} panel the element holding the screenshot
 * @param {number} gap distance between term and panel
 * @returns {'top'|'bottom'}
 */
export function resolvePanelPlacement(hint, panel, gap = 12) {
    const header = document.querySelector('.header');
    const blockedFromTop = (header ? header.offsetHeight : 0) + 8;
    const spaceAbove = hint.getBoundingClientRect().top - blockedFromTop;

    return spaceAbove >= panel.offsetHeight + gap ? 'top' : 'bottom';
}

function open(hint, trigger, panel) {
    hint.classList.add(OPEN_CLASS);
    trigger.setAttribute('aria-expanded', 'true');
    // Measured while visible, otherwise the panel reports a size of zero.
    panel.dataset.placement = resolvePanelPlacement(hint, panel);
    panel.style.setProperty('--tutorial-hint-shift', `${resolvePanelShift(hint, panel)}px`);
}

function close(hint, trigger) {
    hint.classList.remove(OPEN_CLASS);
    trigger.setAttribute('aria-expanded', 'false');
}

/**
 * Wires up the term markup produced by CodeQ\Site\Eel\TutorialHintHelper.
 *
 * @param {ParentNode} root element to search within
 * @returns {number} number of hints that were attached
 */
export function attachTutorialHints(root = document) {
    const hints = root.querySelectorAll('[data-tutorial-hint]');
    const canHover = supportsHover();
    let attached = 0;

    hints.forEach((hint) => {
        const trigger = hint.querySelector('.tutorial-hint__term');
        const panel = hint.querySelector('.tutorial-hint__panel');
        if (!trigger || !panel || hint.dataset.tutorialHintAttached === 'true') {
            return;
        }

        hint.dataset.tutorialHintAttached = 'true';
        attached += 1;

        const panelId = `tutorial-hint-${attached}-${Math.round(performance.now())}`;
        panel.id = panelId;
        trigger.setAttribute('aria-describedby', panelId);

        // Touch browsers still emit mouseenter on tap, so binding both would open the panel
        // on enter and close it again on the click that follows.
        if (canHover) {
            hint.addEventListener('mouseenter', () => open(hint, trigger, panel));
            hint.addEventListener('mouseleave', () => close(hint, trigger));
        }

        // Keyboard users reach the term by tabbing to it.
        hint.addEventListener('focusin', () => open(hint, trigger, panel));
        hint.addEventListener('focusout', (event) => {
            if (!hint.contains(event.relatedTarget)) {
                close(hint, trigger);
            }
        });

        trigger.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                close(hint, trigger);
            }
        });

        if (!canHover) {
            // Without hover the term is the only way in, so it toggles. Tapping it also
            // focuses it, which opens the panel before the click arrives, so the toggle has
            // to compare against the state from before the tap started.
            let wasOpenBeforeTap = false;
            trigger.addEventListener('pointerdown', () => {
                wasOpenBeforeTap = hint.classList.contains(OPEN_CLASS);
            });
            trigger.addEventListener('click', (event) => {
                event.preventDefault();
                if (wasOpenBeforeTap) {
                    close(hint, trigger);
                } else {
                    open(hint, trigger, panel);
                }
            });
        }
    });

    if (attached > 0 && !canHover) {
        // A tapped panel would otherwise stay open until the same term is tapped again.
        document.addEventListener('click', (event) => {
            root.querySelectorAll(`.${OPEN_CLASS}`).forEach((hint) => {
                if (!hint.contains(event.target)) {
                    close(hint, hint.querySelector('.tutorial-hint__term'));
                }
            });
        });
    }

    return attached;
}

if (typeof window !== 'undefined') {
    window.addEventListener('DOMContentLoaded', () => attachTutorialHints(document));
}
